// src/components/shell/popovers/ProfilePopover.tsx
// Avatar trigger + popup containing sign-out (and only sign-out for now).
// Org switching lives in the sibling `OrgPopover` since dev-sync 6dabb83f.
// Sign-out logic absorbs what previously lived in tempLoginButton.tsx.
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { SidebarRailButton } from "@/components/shell/Sidebar/SidebarRailButton";
import { railPopoverPanelStyle, useRailPopover } from "./useRailPopover";

export type ProfilePopoverProps = {
  expanded: boolean;
  onAfterAction?: () => void;
};

function avatarInitials(name: string | null | undefined, email: string | null | undefined): string {
  const cleaned = (name ?? "").trim();
  if (cleaned) {
    const parts = cleaned.split(/\s+/).slice(0, 2);
    return parts.map((p) => p.charAt(0).toUpperCase()).join("");
  }
  const e = (email ?? "").trim();
  if (e) return e.charAt(0).toUpperCase();
  return "•";
}

function avatarChipColor(seed: string): string {
  const palettes = [
    "bg-indigo-500 text-white",
    "bg-emerald-500 text-white",
    "bg-sky-500 text-white",
    "bg-violet-500 text-white",
    "bg-amber-500 text-white",
    "bg-rose-500 text-white",
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palettes[h % palettes.length]!;
}

async function performSignOut() {
  if (!supabase) {
    console.error(
      "Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment."
    );
    return;
  }
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error("Supabase sign-out error:", error);
    return;
  }
  window.location.reload();
}

export function ProfilePopover({ expanded, onAfterAction }: ProfilePopoverProps) {
  const auth = useAuth();
  const { open, toggle, setOpen, triggerRef, panelRef, position } = useRailPopover();

  const fullName = auth.profile?.full_name?.trim() ?? "";
  const email = auth.session?.user?.email ?? auth.profile?.email ?? "";
  const initials = avatarInitials(fullName, email);
  const chipColor = avatarChipColor(fullName || email || "guest");

  return (
    <>
      <SidebarRailButton
        ref={triggerRef}
        expanded={expanded}
        glyph={initials}
        label="Account"
        glyphChipClassName={chipColor}
        active={open}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
      />
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Account"
              className="rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-gray-700 dark:bg-zinc-950 dark:text-gray-100"
              style={railPopoverPanelStyle(position)}
            >
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden
                    className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${chipColor}`}
                  >
                    {initials}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{fullName || "Account"}</div>
                    <div className="truncate text-xs text-gray-500 dark:text-gray-400">{email || ""}</div>
                  </div>
                </div>
              </div>

              <div className="px-2 py-2 space-y-1">
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onAfterAction?.();
                    void performSignOut();
                  }}
                  className="w-full rounded-lg px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40"
                >
                  Sign out
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
